import React from "react";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";
import FontIcon, { ODL_ICONS } from "@odl/core/components/DataDisplay/FontIcon";
import { hideInPrintMixin } from "styles/mixins/hideInPrintMixin";
import { css } from "@emotion/react";

type Props = {
  scrollElement?: HTMLElement;
  scrollPosition: number;
};

const ScrollToTop: React.FC<Props> = ({ scrollElement, scrollPosition }) => {
  // Common
  const { t } = useTranslation();
  const theme = useTheme();

  // Callbacks
  const handleScrollToTop = React.useCallback(() => {
    if (!scrollElement) {
      return;
    }

    scrollElement.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [scrollElement]);

  // Guard
  if (!scrollElement || scrollPosition <= 400) {
    return null;
  }

  return (
    <StyledScrollToTop data-testid={"ScrollToTop"}>
      <StyledScrollToTopButton data-testid={"ScrollToTopButton"} onClick={handleScrollToTop}>
        <StyledActionTitle>{t("Back to Top")}</StyledActionTitle>
        <StyledFontIcon
          name={ODL_ICONS.NAVIGATION_ARROW_UP}
          data-testid={"ScrollToTopIcon"}
          fontSize={14}
          color={theme.palette.objective.blue.main}
        />
      </StyledScrollToTopButton>
    </StyledScrollToTop>
  );
};

const StyledScrollToTop = styled("div")(
  ({ theme }) => css`
    position: fixed;
    bottom: 2px;
    right: 16px;
    z-index: ${theme.zIndex.modal};

    ${hideInPrintMixin()}
  `
);

const StyledScrollToTopButton = styled("button")(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    background-color: transparent;
    border: 0;
    opacity: 100%;
    padding: 8px 0;
    cursor: pointer;
    color: ${theme.palette.primary.main};
    ${theme.mixins.flexGap("6px")}

    &:hover {
      opacity: 100%;
    }
  `
);

const StyledFontIcon = styled(FontIcon)(
  ({ theme }) => css`
    fill: ${theme.palette.objective.background.freshCabbage};
  `
);

const StyledActionTitle = styled("span")(
  ({ theme }) => css`
    font-size: 13px;
    font-weight: 500;
  `
);

export default ScrollToTop;
